import React, { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocalization } from "../../../context/LocalizeContextProvider";
import { getActiveVisitReservation } from "../../../services/ActiveVisitService";
import { getItemOptions } from "../../../services/Handlers/MasterItemsService";
import { getPackages } from "../../../services/Handlers/MasterPackageService";
import { findItemById, formatNumberWithCurrency } from "../../../utils/utils";
import { paymentItemType } from "../../../utils/constants";
import useLocation from "../../../hooks/SatuSehat/useLocation";
import Modal from "../Modal";
import Spinner from "../Spinner";

// Import components
import PatientDataCard from "./components/PatientDataCard";
import ReservationHeader from "./components/ReservationHeader";
import AdditionalSection from "./components/AdditionalSection";
import PaymentSection from "./components/PaymentSection";
import ModalFooterActions from "./components/ModalFooterActions";
import PaymentCorrectionSection from "./components/PaymentCorrectionSection";

import styles from "./ModalDetailReservationV2.module.css";
import Tabs from "../Tabs";
import { ExistingPatient, FormNewPatient } from "../CalendarScheduleDoctor";
import { Form } from "react-final-form";
import usePatientReservationValidation from "../../../utils/validation/usePatientReservationValidation";
import InputField from "../InputField";
import Button from "../Button";
import { useModal } from "../../../context/ModalProvider";

const emptyPaymentItem = {
  package: null,
  item: null,
  qty: 1,
  amount: null,
  typeDiscount: null,
  discount: null,
  isPackage: false,
};

export default function ModalDetailReservationV2({
  show = true,
  handleModal,
  businessId = null,
  dataDetailReservation = {},
  loading = false,
  isPaymentSection = false,
  selectedExistingPatient = null,
  dataFormNew = {},
  dataUsePackage = [],
  dataUseDeposit = [],
  handleChangeForm = () => undefined,
  handleChangeStatusReservation = () => undefined,
  setSelectedExistingPatient = () => undefined,
  handleChangePatientReservation = () => undefined,
  handleSubmitNew = () => undefined,
  handleModalStartVisit = () => undefined,
  handleModalEndVisit = () => undefined,
  handleClickPayment = () => undefined,
  handleStartVisit = () => undefined,
  handleShowModalAlert = () => undefined,
  handleShowModalTransfer = () => undefined,
  handleUsePackage = () => undefined,
  handleDeletePackage = () => undefined,
  handleUndoUsedPackage = () => undefined,
  handleUseDeposit = () => undefined,
  handleUndoUsedDeposit = () => undefined,
  handleRefoundSaldo = () => undefined,
}) {
  const localize = useLocalization();
  const modal = useModal();
  const { dataLocations } = useLocation();
  const validatePatientReservation = usePatientReservationValidation();

  const [detailPatient, setDetailPatient] = useState(null);

  // Options for payment items
  const [itemOptions, setItemOptions] = useState([]);
  const [packageOptions, setPackageOptions] = useState([]);

  // Payment state management like FormPayment
  const [paymentData, setPaymentData] = useState({
    name: "",
    email: "",
    phoneNumber: "",
    method: "",
    cardNumber: "",
    saldo: 0,
    paymentItems: [emptyPaymentItem],
  });
  const [paymentParentData, setPaymentParentData] = useState([]);
  const [nominalUsedBalance, setNominalUsedBalance] = useState(0);

  // Refs to track changes like in FormPayment
  const prevValueSignatureRef = useRef("");
  const prevValueParentSignatureRef = useRef("");

  // Query patient detail
  const { isFetching: isLoadingSingle } = useQuery(
    ["DetailPatientReservation", dataDetailReservation.patientId],
    () => getActiveVisitReservation(dataDetailReservation.patientId),
    {
      enabled: !!dataDetailReservation.patientId,
      onSuccess: (res) => {
        if (res.status === 200) {
          setDetailPatient(res.data);
        }
      },
    }
  );

  // Query item options
  useQuery(["GetItemsOption", businessId], () => getItemOptions(businessId), {
    onSuccess: (res) => {
      if (res.data && res.status === 200) {
        setItemOptions(res.data);
      }
    },
    enabled: !!businessId,
    refetchOnWindowFocus: false,
  });

  // Query package options
  const { isFetching: isLoadingPackage } = useQuery(
    ["PackageList", businessId],
    () => getPackages({ businessId: businessId, size: 999, active: true }),
    {
      onSuccess: (res) => {
        if (res.status === 200 || res.status === 201) {
          setPackageOptions(res?.data.data || []);
        }
      },
      enabled: !!businessId,
    }
  );

  // Initialize payment data from existing payment (like FormPayment)
  useEffect(() => {
    const payment = dataDetailReservation?.payment;

    if (
      !payment ||
      !Array.isArray(payment?.payment_items) ||
      payment?.payment_items?.length === 0
    ) {
      return;
    }

    const valueSignature = JSON.stringify({
      payment_items: payment.payment_items,
      payment_method: payment.payment_method,
      card_number: payment.card_number,
      user_name: payment.user_name,
      user_email: payment.user_email,
      user_phone_number: payment.user_phone_number,
      saldo: payment?.saldo,
    });

    const hasValueChanged = valueSignature !== prevValueSignatureRef.current;

    const canProcess =
      itemOptions.length > 0 &&
      (!isLoadingPackage || packageOptions.length > 0);

    if (
      canProcess &&
      ((paymentData.paymentItems[0].item === null &&
        paymentData.paymentItems[0].package === null) ||
        hasValueChanged)
    ) {
      const paymentItems = [];

      if (
        payment.payment_items.filter(
          (value) => value.type !== paymentItemType.CORRECTION
        ).length > 0
      ) {
        payment.payment_items
          .filter((value) => value.type !== paymentItemType.CORRECTION)
          .forEach((item) => {
            let selectedPackage = null;

            if (item.is_package) {
              const findSelectedPackage = packageOptions.find(
                (pck) => pck.id === item.package_id
              );

              if (findSelectedPackage) {
                selectedPackage = {
                  value: findSelectedPackage.id,
                  label: findSelectedPackage.name,
                };
              }
            }

            paymentItems.push({
              item: findItemById(
                itemOptions,
                item.item_id || item.deposit_item_id
              ),
              package: selectedPackage,
              isPackage: item.is_package || false,
              isDeposit: item.is_deposit || false,
              qty: item.is_package ? 1 : item.quantity,
              amount: item.amount,
              typeDiscount: item.type_discount,
              discount: item.discount,
              additionalId: item.package_id || item.deposit_id,
              isRedo: (item.is_package || item.is_deposit) && item.amount === 0,
            });
          });
      } else {
        paymentItems.push(emptyPaymentItem);
      }

      setPaymentData({
        name: payment?.user_name || "",
        email: payment?.user_email || "",
        phoneNumber: payment?.user_phone_number || "",
        method: payment.payment_method,
        cardNumber: payment.card_number,
        saldo: payment?.saldo || 0,
        paymentItems,
      });

      prevValueSignatureRef.current = valueSignature;
    }
  }, [
    dataDetailReservation?.payment,
    itemOptions,
    packageOptions,
    isLoadingPackage,
    paymentData.paymentItems,
  ]);

  // Initialize parent payment data (like FormPayment)
  useEffect(() => {
    const paymentParent = dataDetailReservation?.payment_parent;

    if (
      !paymentParent ||
      !Array.isArray(paymentParent) ||
      paymentParent.length === 0
    ) {
      return;
    }

    const valueParentSignature = JSON.stringify(
      paymentParent.map((parent) => ({
        payment_items: parent.payment_items,
        doctor_id: parent.doctor_id,
      }))
    );

    const hasValueParentChanged =
      valueParentSignature !== prevValueParentSignatureRef.current;

    const canProcess =
      itemOptions.length > 0 &&
      (!isLoadingPackage || packageOptions.length > 0);

    if (
      canProcess &&
      (paymentParentData.length === 0 || hasValueParentChanged)
    ) {
      const parentPayments = paymentParent.map((parent) => {
        const paymentItems = [];

        if (
          parent.payment_items &&
          parent.payment_items.filter(
            (item) => item.type !== paymentItemType.CORRECTION
          ).length > 0
        ) {
          parent.payment_items
            .filter((item) => item.type !== paymentItemType.CORRECTION)
            .forEach((item) => {
              let selectedPackage = null;

              if (item.is_package) {
                const findSelectedPackage = packageOptions.find(
                  (pck) => pck.id === item.package_id
                );
                if (findSelectedPackage) {
                  selectedPackage = {
                    value: findSelectedPackage.id,
                    label: findSelectedPackage.name,
                  };
                }
              }

              paymentItems.push({
                item: findItemById(
                  itemOptions,
                  item.item_id || item.deposit_item_id
                ),
                qty: item.is_package ? 1 : item.quantity,
                amount: item.amount,
                typeDiscount: item.type_discount,
                discount: item.discount,
                package: selectedPackage,
                isPackage: item.is_package || false,
                isDeposit: item.is_deposit || false,
                additionalId: item.package_id || item.deposit_id,
              });
            });
        } else {
          paymentItems.push(emptyPaymentItem);
        }

        const doctorName = `Doctor ${parent.doctor_id}`; // You can get from context or pass as prop

        return {
          doctorName,
          paymentItems,
        };
      });

      setPaymentParentData(parentPayments);
      prevValueParentSignatureRef.current = valueParentSignature;
    }
  }, [
    dataDetailReservation?.payment_parent,
    itemOptions,
    packageOptions,
    isLoadingPackage,
    paymentParentData.length,
  ]);

  // Computed values
  const isWaitingMedicine =
    dataDetailReservation.status?.label === "Menunggu Obat";
  const isRequestMedicine =
    dataDetailReservation.status?.label === "Obat Disiapkan";
  const isMedicineReady = dataDetailReservation.status?.label === "Obat Siap";
  const isActive =
    dataDetailReservation.status?.label === "Aktif" ||
    isWaitingMedicine ||
    isMedicineReady ||
    isRequestMedicine;
  const isDone = dataDetailReservation.status?.label === "Selesai";
  const isWaitingPayment =
    dataDetailReservation.status?.label === "Belum Bayar";
  const isReservation = dataDetailReservation.status?.label === "Reservasi";
  const isPatientNew = dataDetailReservation?.is_patient_new || false;

  // Calculate selected location from V1 logic
  const selectedLocation = dataLocations.find(
    (value) => value.id === dataFormNew.locationId
  )
    ? {
        value: dataLocations.find(
          (value) => value.id === dataFormNew.locationId
        ).id,
        label: dataLocations.find(
          (value) => value.id === dataFormNew.locationId
        ).name,
      }
    : dataFormNew.locationId
    ? dataFormNew.locationId
    : null;

  // Helper functions from V1
  const handleCheckUsedPackage = (package_id) => {
    const dataPaymentItem = dataDetailReservation?.payment?.payment_items || [];
    const filterPackageItem = dataPaymentItem.filter(
      (item) => item.package_id === package_id && item.amount === 0
    );
    return filterPackageItem.length > 0;
  };

  const checkUsedPackageReservation = (package_id) => {
    return dataUsePackage.includes(package_id);
  };

  // Transform activePackages data for AdditionalSection
  const packageItems = (dataDetailReservation?.actived_packages || []).map(
    (item) => ({
      id: item.id,
      package_id: item.package_id,
      name: item.package_name,
      price: item.price,
      session: checkUsedPackageReservation(item.package_id)
        ? +item.remaining_number_of_visit - 1
        : item.remaining_number_of_visit,
      isUsed: handleCheckUsedPackage(item.package_id),
      isUsedInReservation: checkUsedPackageReservation(item.package_id),
      // Pass handlers
      handleUsePackage,
      handleDeletePackage,
      handleUndoUsedPackage,
    })
  );

  const handleCloseModal = () => {
    handleModal(null);
    setDetailPatient(null);
    setPaymentData({
      name: "",
      email: "",
      phoneNumber: "",
      method: "",
      cardNumber: "",
      paymentItems: [emptyPaymentItem],
    });
    setPaymentParentData([]);
  };

  // Payment handlers (like FormPayment)
  const handleAddPaymentItem = () => {
    const dataPaymentItems = [...paymentData.paymentItems];
    dataPaymentItems.push(emptyPaymentItem);

    setPaymentData((prevState) => ({
      ...prevState,
      paymentItems: dataPaymentItems,
    }));
  };

  const handleAddPaymentPackage = () => {
    const dataPaymentItems = [...paymentData.paymentItems];
    dataPaymentItems.push({ ...emptyPaymentItem, isPackage: true });

    setPaymentData((prevState) => ({
      ...prevState,
      paymentItems: dataPaymentItems,
    }));
  };

  const handleDeletePaymentItem = (index) => {
    const dataPaymentItems = [...paymentData.paymentItems];
    dataPaymentItems.splice(index, 1);

    setPaymentData((prevState) => ({
      ...prevState,
      paymentItems: dataPaymentItems,
    }));
  };

  const handleChangePaymentForm = (name, value, nameItem, indexItem) => {
    const dataPayment = JSON.parse(JSON.stringify({ ...paymentData }));

    if (!nameItem && indexItem === undefined) {
      dataPayment[name] = value;
    } else {
      dataPayment[name].forEach((item, index) => {
        if (index === indexItem) {
          if (nameItem === "item") {
            const selectedItem = findItemById(itemOptions, value.value);
            item["amount"] = selectedItem.price;
          }
          if (nameItem === "package") {
            const selectedPackage = packageOptions.find(
              (pck) => pck.id === value.value
            );
            if (selectedPackage) {
              item["amount"] = selectedPackage.price;
              item["qty"] = 1;
            }
          }
          if (nameItem === "qty") {
            const selectedItem = findItemById(itemOptions, item["item"].value);
            item["amount"] = selectedItem.price * value;
          }

          item[nameItem] = value;
        }
      });
    }
    setPaymentData(dataPayment);
  };

  const handleChangePaymentSaldo = (value) => {
    const dataPayment = JSON.parse(JSON.stringify({ ...paymentData }));
    const totalAmount = getTotalAmount();
    let usedSaldo = 0;

    if (value < 0 || value > dataDetailReservation?.wallet_balance) {
      usedSaldo = dataDetailReservation?.wallet_balance || 0;
    } else {
      usedSaldo = value;
    }

    if (usedSaldo > totalAmount) {
      usedSaldo = totalAmount;
    }

    setNominalUsedBalance(usedSaldo);
    setPaymentData({ ...dataPayment, saldo: usedSaldo });
  };

  // const handleAddSaldo = () => {
  //   let usedSaldo =
  //     (Number(paymentData.saldo) || 0) + (Number(nominalUsedBalance) || 0);
  //   setPaymentData((prevState) => ({ ...prevState, saldo: usedSaldo }));
  // };

  const handlePaymentSubmit = (data) => {
    const dataPaymentCorrection =
      dataDetailReservation?.payment?.payment_items
        ?.filter((value) => value.type === paymentItemType.CORRECTION)
        ?.map((item) => ({
          itemId: item.item_id,
          itemName: item.item_name_original || item.item_name,
          qty: item.quantity,
          amount: item.amount,
          typeDiscount: null,
          discount: null,
          type: item.type,
        })) || [];

    const dataPaymentItems = data.paymentItems.map((item) => ({
      ...item,
      itemId: item?.item?.value || null,
      itemName: item?.item?.label || null,
      packageId: item?.package?.value || null,
      packageName: item?.package?.label || null,
      type: paymentItemType.CONSULTATION,
    }));

    // Calculate total amount
    const totalAmount = paymentData.paymentItems
      .filter((item) => item.amount !== null)
      .reduce((sum, current) => {
        const amount = Number(current.amount) || 0;
        const discountType = current.typeDiscount;
        const discount = Number(current.discount) || 0;

        let discountedAmount = amount;

        if (discountType === "PERCENTAGE" && discount > 0) {
          discountedAmount -= (amount * discount) / 100;
        } else if (discountType === "NUMBER" && discount > 0) {
          discountedAmount -= discount;
        }

        return sum + Math.max(discountedAmount, 0);
      }, 0);

    const formattedData = {
      ...data,
      saldo: Number(nominalUsedBalance),
      totalSaldo: Number(paymentData?.saldo),
      totalAmountCorrection:
        dataDetailReservation?.payment?.total_amount_correction || 0,
      totalAmount: totalAmount - (Number(paymentData?.saldo) || 0),
      paymentItems: [...dataPaymentItems, ...dataPaymentCorrection],
    };

    handleClickPayment(formattedData);
  };

  const handleUndoItem = (additionalId, type) => {
    if (type === "package") {
      handleUndoUsedPackage(additionalId);
    } else if (type === "deposit") {
      handleUndoUsedDeposit(additionalId);
    }
  };

  const getTotalAmount = () => {
    const totalAmount = paymentData.paymentItems
      .filter((item) => item.amount !== null)
      .reduce((sum, current) => {
        const amount = Number(current.amount) || 0;
        const discountType = current.typeDiscount;
        const discount = Number(current.discount) || 0;

        let discountedAmount = amount;

        if (discountType === "PERCENTAGE" && discount > 0) {
          discountedAmount -= (amount * discount) / 100;
        } else if (discountType === "NUMBER" && discount > 0) {
          discountedAmount -= discount;
        }

        return sum + Math.max(discountedAmount, 0);
      }, 0);

    return totalAmount;
  };

  const showPaymentSection = isActive || isDone || isWaitingPayment;
  const isNoPatient =
    !dataDetailReservation.patientId && !isLoadingSingle && !loading;

  const mainContentClass = showPaymentSection
    ? `${styles.mainContent} ${styles.mainContentInactive}`
    : `${styles.mainContent} ${styles.mainContentInactive}`;

  const leftColumnClass = showPaymentSection
    ? `${styles.leftColumn} ${styles.leftColumnInactive}`
    : `${styles.leftColumn} ${styles.leftColumnInactive}`;

  const currentSignature = JSON.parse(prevValueSignatureRef.current || "{}");

  return (
    <Modal
      visible={!!show}
      title={
        isPaymentSection
          ? localize.getText("paymentDetail")
          : localize.getText("reservationDetail")
      }
      classNameWrapper="d-flex flex-column gap-4"
      size="lg"
      dialogClassName="max-w-1200"
      handleHideModal={() => handleCloseModal()}
    >
      <Spinner loading={loading || isLoadingSingle}>
        <div className={styles.container}>
          <div className={mainContentClass}>
            <div className={leftColumnClass}>
              <ReservationHeader
                dataDetailReservation={dataDetailReservation}
                selectedLocation={selectedLocation}
                dataLocations={dataLocations}
                handleChangeForm={handleChangeForm}
                isReservation={isReservation}
              />

              <PatientDataCard
                isActive={showPaymentSection}
                isPatientNew={isPatientNew}
                dataDetailReservation={dataDetailReservation}
                detailPatient={detailPatient}
              />

              {dataDetailReservation.patientId && (
                <div className={styles.additionalSections}>
                  <div className={styles.additionalSection}>
                    <div className={styles.additionalSectionHeader}>
                      <i
                        className={`fas fa-wallet ${styles.depositIcon} ${styles.sectionIcon}`}
                      />
                      <p className={styles.sectionTitle}>
                        {localize.getText("balance")}
                      </p>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        justifyContent: "space-between",
                        width: "100%",
                      }}
                    >
                      <p style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>
                        {formatNumberWithCurrency(
                          dataDetailReservation?.wallet_balance
                        )}
                      </p>
                      {!!currentSignature.saldo && (
                        <Button
                          onClick={() =>
                            modal.showConfirmation({
                              title: localize.getText("areYouSure"),
                              message: localize.getText("cancelUsedBalance"),
                              onConfirm: () => {
                                handleRefoundSaldo();
                                setNominalUsedBalance(null);
                              },
                            })
                          }
                          tooltip={localize.getText("cancelUsedBalance")}
                          variant="info"
                          size="sm"
                        >
                          <i className="fas fa-undo" />
                        </Button>
                      )}
                    </div>

                    {dataDetailReservation?.wallet_balance > 0 &&
                      !currentSignature.saldo &&
                      !isDone &&
                      isActive && (
                        <div
                          style={{
                            width: "100%",
                            display: "flex",
                            flexDirection: "column",
                            gap: 12,
                          }}
                        >
                          <div
                            style={{ display: "flex", flexDirection: "column" }}
                          >
                            <InputField
                              placeholder="Masukkan Nominal"
                              size="small"
                              type="number"
                              onChange={(value) =>
                                handleChangePaymentSaldo(value)
                              }
                              value={nominalUsedBalance}
                            />
                            <p style={{ margin: 0, fontSize: 8 }}>
                              <span style={{ color: "#fc2e53" }}>*</span>{" "}
                              Maksimal:{" "}
                              {formatNumberWithCurrency(
                                dataDetailReservation?.wallet_balance
                              )}
                            </p>
                          </div>
                        </div>
                      )}
                  </div>

                  {/* Only show package section if there are active packages */}
                  <AdditionalSection
                    title={localize.getText("activePackages")}
                    iconClass={`fas fa-box-open ${styles.packageIcon}`}
                    items={packageItems}
                    tooltipCancelActive={localize.getText(
                      "cancelActivePackage"
                    )}
                    isPackageSection
                    isReservation={isReservation}
                    disabled={isDone || isWaitingPayment}
                  />
                </div>
              )}
            </div>

            {/* Payment Section - Show for active, done, or waiting payment */}
            {showPaymentSection && (
              <div
                style={{ flexDirection: "column", display: "flex", gap: 24 }}
              >
                <PaymentSection
                  businessId={businessId}
                  paymentData={paymentData}
                  paymentParentData={paymentParentData}
                  onSubmit={handlePaymentSubmit}
                  itemOptions={itemOptions}
                  packageOptions={packageOptions}
                  disabledForm={isDone}
                  loading={loading}
                  handleAddPaymentItem={handleAddPaymentItem}
                  handleAddPaymentPackage={handleAddPaymentPackage}
                  handleDeletePaymentItem={handleDeletePaymentItem}
                  handleChangeForm={handleChangePaymentForm}
                  handleUndoItem={handleUndoItem}
                />

                {isPaymentSection && (
                  <PaymentCorrectionSection
                    businessId={businessId}
                    paymentData={dataDetailReservation?.payment}
                    onSubmit={handleClickPayment}
                    disabledForm={false}
                    loading={loading}
                  />
                )}
              </div>
            )}

            {isNoPatient && (
              <Tabs
                items={[
                  {
                    label: localize.getText("existingPatient"),
                    key: "1",
                    children: (
                      <div className="d-flex flex-column gap-4">
                        <ExistingPatient
                          defaultForm={{
                            name: dataDetailReservation.name,
                            phoneNumber: dataDetailReservation.phoneNumber,
                            date: dataDetailReservation.dateOfBirth,
                          }}
                          selectedExistingPatient={selectedExistingPatient}
                          setSelectedExistingPatient={
                            setSelectedExistingPatient
                          }
                        />
                        <ModalFooterActions
                          dataDetailReservation={dataDetailReservation}
                          detailPatient={detailPatient}
                          selectedExistingPatient={selectedExistingPatient}
                          dataFormNew={dataFormNew}
                          handleModal={handleCloseModal}
                          handleChangeStatusReservation={
                            handleChangeStatusReservation
                          }
                          handleClickSave={handleChangePatientReservation}
                          handleModalStartVisit={handleModalStartVisit}
                          handleModalEndVisit={handleModalEndVisit}
                          handleStartVisit={handleStartVisit}
                          handleShowModalAlert={handleShowModalAlert}
                          handleShowModalTransfer={handleShowModalTransfer}
                          isPaymentSection={isPaymentSection}
                          disableSave={!selectedExistingPatient?.id}
                          disabledVisit={
                            !dataDetailReservation.patientId
                              ? !selectedExistingPatient?.id
                              : false
                          }
                        />
                      </div>
                    ),
                  },
                  {
                    label: localize.getText("newPatient"),
                    key: "2",
                    children: (
                      <Form
                        initialValues={{ ...dataFormNew }}
                        validate={validatePatientReservation}
                        onSubmit={handleSubmitNew}
                      >
                        {({ handleSubmit, values, valid }) => (
                          <form
                            className="row row-gap-4"
                            style={{ rowGap: 12 }}
                            onSubmit={handleSubmit}
                          >
                            <FormNewPatient
                              value={dataFormNew}
                              handleChangeForm={handleChangeForm}
                            />
                            <ModalFooterActions
                              isNew
                              disabledVisit={!valid}
                              dataDetailReservation={dataDetailReservation}
                              detailPatient={detailPatient}
                              selectedExistingPatient={selectedExistingPatient}
                              dataFormNew={dataFormNew}
                              handleModal={handleCloseModal}
                              handleChangeStatusReservation={
                                handleChangeStatusReservation
                              }
                              handleModalStartVisit={handleModalStartVisit}
                              handleModalEndVisit={handleModalEndVisit}
                              handleStartVisit={handleStartVisit}
                              handleShowModalAlert={handleShowModalAlert}
                              handleShowModalTransfer={handleShowModalTransfer}
                              isPaymentSection={isPaymentSection}
                            />
                          </form>
                        )}
                      </Form>
                    ),
                  },
                ]}
              />
            )}
          </div>

          {!isNoPatient && (
            <>
              <hr className={styles.divider} />

              {/* Updated Footer with complete functionality */}
              <ModalFooterActions
                dataDetailReservation={dataDetailReservation}
                detailPatient={detailPatient}
                selectedExistingPatient={selectedExistingPatient}
                dataFormNew={dataFormNew}
                handleModal={handleCloseModal}
                handleChangeStatusReservation={handleChangeStatusReservation}
                handleClickSave={handleChangePatientReservation}
                handleModalStartVisit={handleModalStartVisit}
                handleModalEndVisit={handleModalEndVisit}
                handleStartVisit={handleStartVisit}
                handleShowModalAlert={handleShowModalAlert}
                handleShowModalTransfer={handleShowModalTransfer}
                isPaymentSection={isPaymentSection}
              />
            </>
          )}
        </div>
      </Spinner>
    </Modal>
  );
}
